/**
 * Bildliche Erklaerung fuer „Verschluesselung erklaert“: was beim Hoch- und Herunterladen passiert.
 *
 * Oben: Schluessel schliesst die Datei im Browser ab → sie wandert unlesbar in den Tresor.
 * Unten: sie kommt unlesbar zurueck → der Schluessel schliesst sie im Browser wieder auf → Datei.
 * Das wirre Knaeuel steht nur im Bild fuer „unlesbar“, im Text kommt es bewusst nicht vor.
 *
 * Bewusst KEIN erfundener Zeichensalat (keine Fake-Hex-Ziffern): das Knaeuel ist ein Bild fuer
 * „unlesbar“, keine Behauptung ueber echte Bytes. Echte Bytes zeigt der Maschinenraum.
 * Bewegung nur per CSS (index.css, .ef-*), bei prefers-reduced-motion steht alles still.
 */

import './EncryptionFlow.css';

function DocIcon({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`} className="ef-stroke">
      <path d="M0 0 h22 l10 10 v32 h-32 z" />
      <path d="M22 0 v10 h10" />
      <path d="M7 19 h18 M7 26 h18 M7 33 h12" />
    </g>
  );
}

function KeyIcon({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`} className="ef-stroke ef-accent">
      <circle cx="9" cy="9" r="7" />
      <path d="M15 13 l16 16 M25 23 l-4 4 M29 27 l-4 4" />
    </g>
  );
}

// Vorhaengeschloss am Browser: schnappt beim Hochladen zu, wird beim Herunterladen aufgeschlossen.
// Der Buegel ist eine eigene Gruppe, die die Animation anhebt (offen) oder absenkt (zu).
function Padlock({ x, y, shackleClass }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className={shackleClass}><path d="M5 12 V7 a7 7 0 0 1 14 0 V12" className="ef-stroke ef-accent" /></g>
      <rect x="1" y="12" width="22" height="17" rx="4" className="ef-lock-body" />
      <circle cx="12" cy="20.5" r="2.2" className="ef-lock-hole" />
    </g>
  );
}

function Yarn({ className = '' }) {
  // Ein Knaeuel aus ueberkreuzten Bahnen: sichtbar „verknotet“, ohne Text darin.
  return (
    <g className={`ef-yarn ${className}`}>
      <circle cx="0" cy="0" r="16" className="ef-yarn-ball" />
      <path d="M-14 -4 C -6 -14, 8 -12, 14 -2" />
      <path d="M-13 6 C -4 -6, 8 -6, 13 8" />
      <path d="M-6 -14 C 2 -4, 2 6, -4 14" />
      <path d="M5 -14 C -3 -4, -1 8, 7 13" />
      <path d="M-15 0 C -5 4, 5 -4, 15 0" />
    </g>
  );
}

function Browser({ x, y, label }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x="0" y="0" width="120" height="76" rx="10" className="ef-frame" />
      <path d="M0 18 h120" className="ef-stroke" />
      <circle cx="11" cy="9" r="2.4" className="ef-dot" />
      <circle cx="19" cy="9" r="2.4" className="ef-dot" />
      <circle cx="27" cy="9" r="2.4" className="ef-dot" />
      <text x="60" y="94" textAnchor="middle" className="ef-label">{label}</text>
    </g>
  );
}

function Safe({ x, y }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x="0" y="0" width="96" height="76" rx="10" className="ef-frame ef-safe" />
      <rect x="10" y="10" width="76" height="56" rx="6" className="ef-stroke" />
      <circle cx="48" cy="38" r="13" className="ef-stroke ef-accent" />
      <path d="M48 25 v6 M48 45 v6 M35 38 h6 M55 38 h6" className="ef-stroke ef-accent" />
      <text x="48" y="94" textAnchor="middle" className="ef-label">Tresor, Deutschland</text>
    </g>
  );
}

export default function EncryptionFlow() {
  // Die Animation verschiebt die Gruppen mit den Klassen ef-*. Ihre Position kommt deshalb aus einer
  // ANDEREN, inneren Gruppe: CSS-transform wuerde ein transform-Attribut am selben Element ersetzen.
  return (
    <figure className="ef-wrap" aria-label="Verschlüsselung im Browser, unlesbare Übertragung, Entschlüsselung beim Herunterladen">
      <svg viewBox="0 0 440 250" className="ef-svg" role="img">
        {/* Hochladen */}
        <text x="0" y="12" className="ef-row">Hochladen</text>
        <Browser x={0} y={22} label="Ihr Gerät" />
        <g className="ef-up-doc"><DocIcon x={24} y={47} /></g>
        <g className="ef-up-key"><KeyIcon x={74} y={52} /></g>
        <g className="ef-up-lock"><Padlock x={44} y={64} shackleClass="ef-up-shackle" /></g>
        <path d="M128 64 H 336" className="ef-track" />
        <g className="ef-up-yarn"><Yarn /></g>
        <Safe x={344} y={22} />

        {/* Herunterladen */}
        <text x="0" y="140" className="ef-row">Herunterladen</text>
        <Browser x={0} y={150} label="Ihr Gerät" />
        <g className="ef-down-doc"><DocIcon x={24} y={175} /></g>
        <g className="ef-down-key"><KeyIcon x={74} y={180} /></g>
        <g className="ef-down-lock"><Padlock x={44} y={192} shackleClass="ef-down-shackle" /></g>
        <path d="M128 192 H 336" className="ef-track" />
        <g className="ef-down-yarn"><Yarn /></g>
        <Safe x={344} y={150} />
      </svg>
      <figcaption className="ef-caption">
        <span><strong>Auf Ihrem Gerät</strong> wird jede Datei mit Ihrem Schlüssel verschlüsselt, bevor sie losgeht.</span>
        <span><strong>Unterwegs und im Tresor</strong> ist sie verschlüsselt und unlesbar. Wir sehen nur das.</span>
        <span><strong>Beim Herunterladen</strong> schließt Ihr Schlüssel sie in Ihrem Browser wieder auf.</span>
      </figcaption>
    </figure>
  );
}
